import { tryParseDate } from '@shared/functions/try-parse-date';
import { ElectricMeterCabinet, IElectricMeterCabinet } from '@shared/models/electric-meter-cabinet';
import { Boat, IBoat } from '@shared/models/boat';
import { Reading } from '@shared/models/electric-meter-reading';
import { ElectricMeterAssignment, IElectricMeterAssignment } from '@shared/models/electric-meter-assignment';
import { Berth, IBerth } from '@shared/models/berth';

export enum ElectricMeterFuseType {
    EIGHT_A_230_BLUE = '8A/230V-blau',
    TEN_A_230 = '10A/230V',
    SIXTEEN_A_230_BLUE = '16A/230V-blau',
    THIRTY_TWO_A_230_BLUE = '32A/230V-blau',
    FIFTY_A_230_BLUE = '50A/230V-blau',
    SIXTEEN_A_230_RED = '16A/400V-rot',
    THIRTY_TWO_A_230_RED = '32A/400V-rot',
    SIXTY_THREE_A_230_RED = '63A/400V-rot',
    ONE_TWENTY_FIVE_A_230_RED = '125A/400V-rot',
    FIFTEEN_A_120V = '15A/120V',
    THIRTY_A_120V = '30A/120V',
    FIFTY_A_125_250V = '50A/125-250V',
    HUNDRED_A_120_240V = '100A/120-240V',
    TWO_HUNDRED_A_480V = '200A/480V',
    TWO_HUNDRED_A_400V_CAM_LOCK = '200A/400V-CamLock',
    FOUR_HUNDRED_A_POWERLOCK = '400A-Powerlock',
    SIX_SIXTY_A_POWERLOCK = '660A-Powerlock',
    EIGHT_HUNDRED_A_400V_LVSC = '800A/400V-LVSC',
}

export enum ElectricMetersApiStatus {
    NO_CONNECTION = 'NO_CONNECTION',
    OFF = 'OFF',
    ON = 'ON',
}

export interface IElectricMeterRaw {
    id?: number;
    handle: string;
    manufacturerHandle: string;
    installationDate: string;
    meterReading: number;
    lastReadingOn: string;
    fuse: ElectricMeterFuseType;
    connector: string;
    keyHandle: string; // Hat jeder Zähler nochmal einen eigenen Schlüssel? Also Schrankschlüssel + Zählerschlüssel?
    note?: string;
    apiSwitchedOn: boolean | number | null;
    apiUpdatedOn: Date;
    hasDualUse?: boolean | number;

    cabinet: IElectricMeterCabinet;
    boats: IBoat[];
    readings: any[];
    boatAssignments: IElectricMeterAssignment[];
    defaultBerthAssignment: IBerth;

    switchStatus?: ElectricMetersApiStatus;
}

export interface IElectricMeter {
    id?: number;
    handle: string;
    manufacturerHandle: string;
    installationDate: Date;
    meterReading: number;
    lastReadingOn: Date;
    fuse: ElectricMeterFuseType;
    connector: string;
    keyHandle: string; // Hat jeder Zähler nochmal einen eigenen Schlüssel? Also Schrankschlüssel + Zählerschlüssel?
    note?: string;
    apiSwitchedOn: boolean;
    apiUpdatedOn: Date;
    hasDualUse?: boolean;

    cabinet: IElectricMeterCabinet;
    boats: IBoat[];
    readings: any[];
    boatAssignments: IElectricMeterAssignment[];
    defaultBerthAssignment: IBerth;

    switchStatus?: ElectricMetersApiStatus;
}

export class ElectricMeter {
    cabinet: ElectricMeterCabinet;
    meterReading: number;
    lastReadingOn: Date;
    connector: string;
    fuse: ElectricMeterFuseType;
    handle: string;
    id: number;
    installationDate: Date;
    keyHandle: string;
    manufacturerHandle: string;
    fullName: string;
    note: string;
    apiSwitchedOn?: boolean;
    apiUpdatedOn: Date;
    hasDualUse: boolean;
    boats: Boat[];
    readings: Reading[];
    boatAssignments: ElectricMeterAssignment[];
    defaultBerthAssignment: Berth;
    switchStatus?: ElectricMetersApiStatus;

    // helper
    hasOpenReading?: boolean;

    constructor(electricMeter = {} as (IElectricMeter | IElectricMeterRaw), cabinetHasProvider?: boolean) {
        this.meterReading = electricMeter.meterReading;
        this.lastReadingOn = tryParseDate(electricMeter.lastReadingOn);
        this.connector = electricMeter.connector;
        this.fuse = electricMeter.fuse;
        this.handle = electricMeter.handle;
        this.fullName = (electricMeter.cabinet?.handle + ' ' + electricMeter.handle).trim();
        this.id = electricMeter.id;
        this.installationDate = tryParseDate(electricMeter.installationDate);
        this.keyHandle = electricMeter.keyHandle;
        this.manufacturerHandle = electricMeter.manufacturerHandle;
        this.note = electricMeter.note;

        const rawApiSwitchedOn: any = electricMeter.apiSwitchedOn;
        const hasProvider = cabinetHasProvider !== undefined ? cabinetHasProvider : !!electricMeter.cabinet?.provider;

        if (!hasProvider) {
            this.apiSwitchedOn = undefined;
        } else if (rawApiSwitchedOn === 1 || rawApiSwitchedOn === '1' || rawApiSwitchedOn === true) {
            this.apiSwitchedOn = true;
        } else if (rawApiSwitchedOn === 0 || rawApiSwitchedOn === '0' || rawApiSwitchedOn === false) {
            this.apiSwitchedOn = false;
        } else {
            this.apiSwitchedOn = undefined;
        }

        this.apiUpdatedOn = electricMeter.apiUpdatedOn;
        this.hasDualUse = !!electricMeter.hasDualUse;
        this.cabinet = electricMeter.cabinet ? new ElectricMeterCabinet(electricMeter.cabinet) : null;
        this.boats = (electricMeter.boats || []).map(boat => new Boat(boat));
        this.readings = (electricMeter.readings || []).map(reading => new Reading(reading));
        this.boatAssignments = (electricMeter.boatAssignments || []).map(assignment => new ElectricMeterAssignment(assignment));
        this.defaultBerthAssignment = electricMeter.defaultBerthAssignment ? new Berth(electricMeter.defaultBerthAssignment) : null;
        this.switchStatus = electricMeter.switchStatus;
    }

    get apiSwitchedOnFilter(): 'EIN' | 'AUS' | undefined {
        if (!this.cabinet?.provider) {
            return undefined;
        }

        if (this.apiSwitchedOn) {
            return 'EIN';
        }

        return 'AUS';
    }

    toString(): string {
        return this.fullName;
    }

    valueOf(): number {
        return this.id;
    }

}

